import {
  AliasType,
  Broker,
  BrokerAgency,
  IBrokerAgent,
  LicenseType,
  SubmissionStatus,
  SubmissionSubStatus,
  useGetBrokerLicense,
  useGetBrokerOptions,
  useGetBrokerageAddress,
  useGetBrokerageLicense,
  useGetEntitiesBrokerAgencies,
} from '@converge/graphql';
import {
  CountryFormSelectField,
  FormContainerWithHeader,
  FormNumberInputAdornmentField,
  FormSearchableSelectField,
  NewFormTextField,
  NewFromSelectField,
  RegionFormSelectField,
} from '../../../../presentation';
import { getFieldProps, isUnitedStatesOfAmericaLabel } from '@converge/data';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { capitalizeFirstLetter } from '@converge/utils';
import { useFormContext } from 'react-hook-form';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { ISubmissionProps } from '../underwriting.submission.detail.overview';

interface IProps {
  isReadonly?: boolean;
}

export const UnderwritingOverviewBrokerInfoFormSection = ({
  isReadonly,
}: IProps) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext();
  const [regionName, setRegionName] = useState<string>('State');

  const { submission } = useOutletContext<ISubmissionProps>();

  const parentBrokageEntityUuid =
    submission?.brokerEntity?.node.parents.edges[0]?.node.uuid;
  const parentBrokageName =
    submission?.brokerEntity?.node.parents.edges[0]?.node.aliases.edges.find(
      (alias) => alias?.node.primary && alias?.node.type === AliasType.LegalName
    )?.node?.name ?? '';
  const country = watch('brokerEntity.primaryAddress.countryName');
  const countryCode = watch('brokerEntity.primaryAddress.iso2Code');

  const brokerAgencyUuid = watch('brokerEntity.uuid');
  const brokerAgentUuid = watch('brokerEntity.primaryContact.uuid');
  const brokerageName = watch('brokerEntity.primaryAlias.name');
  const brokerFirstName = watch('brokerEntity.primaryContact.firstName');
  const brokerLastName = watch('brokerEntity.primaryContact.lastName');
  const insuredRegion = watch('insuredEntity.primaryAddress.region');
  const insuredRegionName = watch('insuredEntity.primaryAddress.regionCode');

  const { brokerLicenses: brokerPncLicenses } = useGetBrokerLicense({
    uuid: brokerAgentUuid,
    licenseType: LicenseType.PropertyAndCasualty,
    regionUuid: insuredRegion,
    enabled: brokerAgentUuid,
  });

  const brokerPncLicensesOptions = useMemo(() => {
    return brokerPncLicenses.map((license) => {
      return {
        ...license,
        label: `${insuredRegionName} #${license.label} (${brokerFirstName} ${brokerLastName})`,
      };
    });
  }, [brokerPncLicenses, insuredRegionName, brokerFirstName, brokerLastName]);

  const { brokerLicenses: brokerSurplusLicenses } = useGetBrokerLicense({
    uuid: brokerAgentUuid,
    licenseType: LicenseType.SurplusLineTax,
    regionUuid: insuredRegion,
    enabled: brokerAgentUuid,
  });

  const brokerSurplusLicensesOptions = useMemo(() => {
    return brokerSurplusLicenses.map((license) => {
      return {
        ...license,
        label: `${insuredRegionName} #${license.label} (${brokerFirstName} ${brokerLastName})`,
      };
    });
  }, [
    brokerSurplusLicenses,
    insuredRegionName,
    brokerFirstName,
    brokerLastName,
  ]);

  const { brokerageLicense } = useGetBrokerageLicense({
    uuid: brokerAgencyUuid,
    regionUuid: insuredRegion,
    licenseType: LicenseType.SurplusLineTax,
  });
  const { brokerageLicense: parentBrokerageLicenses } = useGetBrokerageLicense({
    uuid: parentBrokageEntityUuid,
    regionUuid: insuredRegion,
    licenseType: LicenseType.SurplusLineTax,
    enabled: Boolean(parentBrokageEntityUuid),
  });

  const { brokerageLicense: brokeragePncLicenses } = useGetBrokerageLicense({
    uuid: brokerAgencyUuid,
    regionUuid: insuredRegion,
    licenseType: LicenseType.PropertyAndCasualty,
  });

  const { brokerAgencies } = useGetEntitiesBrokerAgencies({
    uuid: brokerAgencyUuid,
  });

  const { brokers = [], loading: brokersLoading } = useGetBrokerOptions({
    brokerageUuid: brokerAgencyUuid,
    enabled: true,
  });

  const { brokerageAddress, loading: brokerageAddressLoading } =
    useGetBrokerageAddress({
      brokerageUuid: brokerAgencyUuid,
      enabled: true,
    });

  const setBrokerInfo = useCallback(
    (broker: Broker) => {
      setValue('brokerEntity.primaryContact.uuid', broker.uuid, {
        shouldDirty: true,
      });
      setValue('brokerEntity.primaryContact.email', broker?.email);
      setValue('brokerEntity.primaryContact.phone', broker?.phone);
      setValue('brokerEntity.primaryContact.firstName', broker.firstName);
      setValue('brokerEntity.primaryContact.lastName', broker.lastName);
    },
    [setValue]
  );

  useEffect(() => {
    const sortedBrokers = [...brokers].sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    const brokerExists = sortedBrokers.some(
      (broker) => broker.uuid === brokerAgentUuid
    );

    const firstBroker = sortedBrokers[0];
    if (!brokerExists && firstBroker) {
      setBrokerInfo(firstBroker);
    }
  }, [brokers, brokerAgentUuid, setBrokerInfo]);

  const setBrokerageAddress = useCallback(() => {
    if (brokerageAddress[0]) {
      const {
        primaryStreet1,
        primaryStreet2,
        primaryCity,
        primaryPostalCode,
        primaryStateUuid,
        primaryCountryName,
      } = brokerageAddress[0];
      setValue('brokerEntity.primaryAddress.street1', primaryStreet1);
      setValue('brokerEntity.primaryAddress.street2', primaryStreet2);
      setValue('brokerEntity.primaryAddress.city', primaryCity);
      setValue('brokerEntity.primaryAddress.region', primaryStateUuid);
      setValue('brokerEntity.primaryAddress.country', primaryCountryName);
      setValue('brokerEntity.primaryAddress.postalCode', primaryPostalCode);
    }
  }, [setValue, brokerageAddress]);

  useEffect(() => {
    setBrokerageAddress();
  }, [brokerageAddress, setBrokerageAddress]);

  const brokerageLicenseOptions = useMemo(() => {
    return brokerageLicense.map((license) => {
      return {
        ...license,
        label: `${insuredRegionName} #${license.label} (${brokerageName})`,
      };
    });
  }, [brokerageLicense, insuredRegionName, brokerageName]);

  const parentBrokerageLicenseOptions = useMemo(() => {
    return parentBrokerageLicenses.map((license) => {
      return {
        ...license,
        label: `${insuredRegionName} #${license.label} (${parentBrokageName})`,
      };
    });
  }, [parentBrokerageLicenses, insuredRegionName, parentBrokageName]);

  const brokeragePncLicenseOptions = useMemo(() => {
    return brokeragePncLicenses.map((license) => {
      return {
        ...license,
        label: `${insuredRegionName} #${license.label} (${brokerageName})`,
      };
    });
  }, [brokeragePncLicenses, insuredRegionName, brokerageName]);

  return (
    <>
      <FormContainerWithHeader title={'Brokerage and Broker'}>
        <ChangeAgencyField
          onChange={(agency) => {
            setValue('brokerEntity.uuid', agency?.value);
            setValue('brokerEntity.primaryAlias.name', agency?.label);
            setValue('brokerCommission', agency?.brokerCommission * 100);
          }}
          agencyUuid={brokerAgencyUuid}
          isReadonly={isReadonly}
        />

        <NewFormTextField
          {...getFieldProps(
            'brokerEntity.primaryAddress.street1',
            true,
            'ADDRESS 1'
          )}
          isLoading={brokerageAddressLoading}
        />

        <NewFormTextField
          {...getFieldProps(
            'brokerEntity.primaryAddress.street2',
            true,
            'ADDRESS 2'
          )}
          isLoading={brokerageAddressLoading}
        />
        <NewFormTextField
          {...getFieldProps('brokerEntity.primaryAddress.city', true, 'CITY')}
          isLoading={brokerageAddressLoading}
        />
        {isUnitedStatesOfAmericaLabel(country) ? (
          <RegionFormSelectField
            {...getFieldProps(
              'brokerEntity.primaryAddress.region',
              true,
              capitalizeFirstLetter(regionName)
            )}
            selectedCountry={countryCode}
            setRegionName={setRegionName}
          />
        ) : (
          <NewFormTextField
            {...getFieldProps(
              'brokerEntity.primaryAddress.region',
              true,
              'REGION'
            )}
            isLoading={brokerageAddressLoading}
          />
        )}
        <NewFormTextField
          {...getFieldProps(
            'brokerEntity.primaryAddress.postalCode',
            true,
            'POSTAL CODE'
          )}
          isLoading={brokerageAddressLoading}
        />
        <CountryFormSelectField
          {...getFieldProps(
            'brokerEntity.primaryAddress.country',
            true,

            'COUNTRY'
          )}
        />
        <NewFromSelectField
          {...getFieldProps(
            'propertyAndCasualtyLicenseUuid',
            false,
            'PROPERTY AND CASUALTY LICENSE'
          )}
          options={[
            { label: 'No License', value: 'null' },
            ...brokerPncLicensesOptions,
            ...brokeragePncLicenseOptions,
          ]}
          placeholder="Select Property and Casualty License"
        />

        <NewFromSelectField
          placeholder="Select Surplus Line Tax License"
          {...getFieldProps(
            'surplusLinesTaxLicenseUuid',
            false,
            'SURPLUS LINE TAX LICENSE'
          )}
          options={[
            { label: 'No License', value: 'null' },
            ...brokerSurplusLicensesOptions,
            ...brokerageLicenseOptions,
            ...parentBrokerageLicenseOptions,
          ]}
        />

        <FormNumberInputAdornmentField
          adornmentText={'%'}
          {...getFieldProps('brokerCommission', isReadonly, t('COMMISSION'))}
          decimalScale={2}
          placeholder="0"
          isAllowed={(values) => {
            const { formattedValue, floatValue } = values;
            if (floatValue === null || floatValue === undefined) {
              return formattedValue === '';
            } else {
              return floatValue >= 0 && floatValue <= 100;
            }
          }}
        />
        <hr className="section-content-hr" />

        <ChangeBrokerAgentField
          onChange={(brokerAgent) => {
            setBrokerInfo(brokerAgent);
          }}
          agencyUuid={brokerAgencyUuid}
          agentUuid={brokerAgentUuid}
          isReadonly={isReadonly}
          brokerAgencies={brokerAgencies}
          brokers={brokers}
          loading={brokersLoading}
        />

        <NewFormTextField
          {...getFieldProps(
            'brokerEntity.primaryContact.email',
            true,
            'EMAIL ADDRESS'
          )}
        />
        <NewFormTextField
          {...getFieldProps(
            'brokerEntity.primaryContact.phone',
            true,
            'PHONE NUMBER'
          )}
        />
      </FormContainerWithHeader>
    </>
  );
};

const ChangeBrokerAgentField = ({
  onChange,
  agencyUuid,
  agentUuid,
  isReadonly,
  brokerAgencies,
  brokers,
  loading,
}: {
  agencyUuid: string;
  agentUuid?: string;
  isReadonly: boolean | undefined;
  brokerAgencies: BrokerAgency[];
  brokers: Broker[];
  loading: boolean;
  onChange?: (brokerAgent: Broker) => void;
}) => {
  const { submission } = useOutletContext<ISubmissionProps>();

  const isBrokerEditingDisabled = useMemo(() => {
    const { status, subStatus } = submission || {};
    return (
      status === SubmissionStatus.Quoted ||
      status === SubmissionStatus.Bound ||
      (status === SubmissionStatus.Evaluation &&
        subStatus === SubmissionSubStatus.EvaluationNotEditable)
    );
  }, [submission]);

  const { agency, agents } = useMemo(() => {
    const agency = brokerAgencies.find((e) => e.value === agencyUuid);
    const agents = brokers ?? [];
    return {
      agency,
      agents,
    };
  }, [brokerAgencies, agencyUuid, brokers]);

  const updateAgent = agents.sort((a, b) => a.name.localeCompare(b.name));
  return (
    <FormSearchableSelectField
      minDropdownWidth="530px"
      maxDropdownWidth="750px"
      name="broker"
      label={'BROKER'}
      options={updateAgent}
      value={agentUuid}
      placeholder="Select Broker"
      disabled={!agency || !agents.length || isBrokerEditingDisabled}
      required
      disableClearable
      mapToOption={(agent) => ({
        label: `${agent.name}`,
        value: agent.uuid,
        data: agent,
      })}
      isLoading={loading}
      onChangeOption={(option) => {
        if (option) {
          onChange?.(option.data);
        }
      }}
    />
  );
};

export interface Inode {
  __typename: string;
  uuid: string;
  name: string;
}
export interface Iedges {
  cursor: number;
  node: Inode;
}

export interface ListOption {
  label: string;
  brokerCommission: number;
  primaryAddress: {
    __typename?: string | undefined;
    street1: string;
    street2?: string | null | undefined;
    city: string;
    state: string;
    postalCode: string;
    countries: {
      __typename: string;
      edges: Iedges[];
    };
    regions: {
      __typename: string;
      edges: Iedges[];
    };

    uuid: string;
  };
  value: string;
  contacts: IBrokerAgent[];
}

const ChangeAgencyField = ({
  onChange,
  agencyUuid,

  isReadonly,
}: {
  agencyUuid: string;
  isReadonly: boolean | undefined;
  onChange?: (agency: ListOption) => void;
}) => {
  const { brokerAgencies } = useGetEntitiesBrokerAgencies({});
  const updateBrokerAgencies = brokerAgencies.sort((a, b) =>
    a.label.localeCompare(b.label)
  );

  return (
    <FormSearchableSelectField
      minDropdownWidth="530px"
      maxDropdownWidth="750px"
      name="agency"
      label={'BROKERAGE'}
      value={agencyUuid}
      options={updateBrokerAgencies}
      placeholder="Select Brokerage"
      disabled={!brokerAgencies || isReadonly}
      required
      disableClearable
      mapToOption={(agency) => ({
        label: `${agency.label}`,
        value: agency.value,
        data: agency,
      })}
      onChangeOption={(option: any) => {
        if (option) {
          onChange?.(option?.data);
        }
      }}
    />
  );
};
